import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Switch from '@/components/ui/Switch';
import SegmentedControl from '@/components/ui/SegmentedControl';
import { useSports, useThemesRef } from '@/features/reference/useReference';
import { decodeWorkout, getWorkout, DecodePreview, WorkoutPayload } from '@/api/workoutsService';
import { queryKeys } from '@/lib/queryKeys';
import DecodePreviewTable from './DecodePreviewTable';

interface WorkoutModalProps {
  open: boolean;
  workoutId: string | null;
  onClose: () => void;
  onSubmit: (payload: WorkoutPayload) => Promise<unknown>;
  isSaving: boolean;
}

const AutoManualField = ({
  label,
  autoHint,
  manual,
  onToggle,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  autoHint: string;
  manual: boolean;
  onToggle: (manual: boolean) => void;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <label className="flex items-center gap-2 text-xs text-gray-500">
        {manual ? 'Manuel' : 'Auto'}
        <Switch size="sm" checked={manual} onChange={onToggle} />
      </label>
    </div>
    {manual ? (
      <Textarea rows={2} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <p className="text-xs text-gray-400">{autoHint}</p>
    )}
  </div>
);

const SectionModeField = <T extends string>({
  label,
  options,
  mode,
  onModeChange,
  hints,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  options: { value: T; label: string }[];
  mode: T;
  onModeChange: (mode: T) => void;
  hints: Partial<Record<T, string>>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) => (
  <div className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 dark:border-gray-800">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <SegmentedControl size="sm" value={mode} options={options} onChange={(v) => onModeChange(v as T)} />
    </div>
    {mode === 'MANUAL' ? (
      <Textarea rows={2} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <p className="text-xs text-gray-400">{hints[mode]}</p>
    )}
  </div>
);

const WorkoutModal = ({ open, workoutId, onClose, onSubmit, isSaving }: WorkoutModalProps) => {
  const { data: sports = [] } = useSports();
  const { data: themes = [] } = useThemesRef();
  const isEdit = Boolean(workoutId);

  const { data: detail } = useQuery({
    queryKey: workoutId ? queryKeys.pv2Workout(workoutId) : ['admin', 'pv2', 'workout', 'new'],
    queryFn: () => getWorkout(workoutId as string),
    enabled: Boolean(workoutId) && open,
  });

  const [name, setName] = useState('');
  const [sportIdentifier, setSportIdentifier] = useState('');
  const [themeId, setThemeId] = useState('');
  const [sourcePattern, setSourcePattern] = useState('');
  const [contentText, setContentText] = useState('');
  const [note, setNote] = useState('');
  const [isTest, setIsTest] = useState(false);

  const [warmupMode, setWarmupMode] = useState<'AUTO' | 'MANUAL'>('AUTO');
  const [warmupText, setWarmupText] = useState('');
  const [cooldownMode, setCooldownMode] = useState<'IN_BODY' | 'AUTO' | 'MANUAL'>('AUTO');
  const [cooldownText, setCooldownText] = useState('');
  const [coachManual, setCoachManual] = useState(false);
  const [coachNote, setCoachNote] = useState('');
  const [nutritionManual, setNutritionManual] = useState(false);
  const [nutrition, setNutrition] = useState('');

  const [preview, setPreview] = useState<DecodePreview | null>(null);
  const [decodeError, setDecodeError] = useState('');
  const [decoding, setDecoding] = useState(false);
  const [error, setError] = useState('');
  const lastDecodedRef = useRef<string>('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setPreview(null);
    setDecodeError('');
    lastDecodedRef.current = '';
    const d = workoutId ? detail : null;
    setName(d?.name && d.name !== d.themeLabel ? d.name : '');
    setSportIdentifier(d?.sportIdentifier ?? '');
    setThemeId(d?.themeId ?? '');
    setSourcePattern(d?.sourcePattern ?? '');
    setContentText(d?.contentText ?? '');
    setNote(d?.note ?? '');
    setIsTest(Boolean(d?.isTest));
    setWarmupMode(d?.warmupMode ?? 'AUTO');
    setWarmupText(d?.warmupText ?? '');
    setCooldownMode(d?.cooldownMode ?? 'AUTO');
    setCooldownText(d?.cooldownText ?? '');
    setCoachManual(Boolean(d?.coachNoteOverride));
    setCoachNote(d?.coachNoteOverride ?? '');
    setNutritionManual(Boolean(d?.nutritionOverride));
    setNutrition(d?.nutritionOverride ?? '');
  }, [open, workoutId, detail]);

  const triggerDecode = async (value: string, manual = false) => {
    const pattern = value.trim();
    if (!sportIdentifier || !pattern) {
      if (manual) setDecodeError(!sportIdentifier ? 'Sélectionnez un sport avant de rejouer.' : 'Saisissez une nomenclature.');
      return;
    }
    if (!manual && `${sportIdentifier}::${pattern}` === lastDecodedRef.current) return;
    lastDecodedRef.current = `${sportIdentifier}::${pattern}`;
    setDecodeError('');
    setDecoding(true);
    try {
      const result = await decodeWorkout(pattern, sportIdentifier);
      setPreview(result);
      if (result.matchedThemeId && !themeId) setThemeId(result.matchedThemeId);
    } catch (err) {
      setPreview(null);
      setDecodeError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Décodage impossible.');
    } finally {
      setDecoding(false);
    }
  };

  const handleDecode = () => triggerDecode(sourcePattern, true);

  const baseTitle = isEdit ? 'Modifier la séance' : 'Ajouter une séance';
  const sportLabel = sports.find((s) => s.identifier === sportIdentifier)?.label;
  const themeLabel = themes.find((t) => t.id === themeId)?.label;
  // PRD 15 T6: with a nomenclature, the theme decides; a manual workout keeps its checkbox.
  const testFromTheme = Boolean(sourcePattern.trim());
  const effectiveIsTest = testFromTheme ? themes.find((t) => t.id === themeId)?.identifier === 'TEST' : isTest;
  const subject = name.trim() || themeLabel;
  const title = sportLabel && subject ? `${subject} · ${sportLabel}` : baseTitle;

  const handleSubmit = async () => {
    setError('');
    if (!sportIdentifier) return setError('Le sport est requis.');
    if (!themeId) return setError('La thématique est requise.');
    if (!sourcePattern.trim() && !contentText.trim()) {
      return setError('Saisissez une nomenclature ou un corps de séance.');
    }
    try {
      await onSubmit({
        sportIdentifier,
        themeId,
        name: name.trim() || null,
        sourcePattern: sourcePattern.trim() || null,
        contentText: contentText.trim() || null,
        warmupMode,
        warmupText: warmupMode === 'MANUAL' ? warmupText.trim() || null : null,
        cooldownMode,
        cooldownText: cooldownMode === 'MANUAL' ? cooldownText.trim() || null : null,
        note: note.trim() || null,
        isTest: sportIdentifier === 'PPG' ? false : effectiveIsTest,
        coachNoteOverride: coachManual ? coachNote.trim() || null : null,
        nutritionOverride: nutritionManual ? nutrition.trim() || null : null,
      });
      onClose();
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Enregistrement impossible.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input label="Nom" placeholder="VO2 court" value={name} onChange={(e) => setName(e.target.value)} />
          <Select label="Sport" value={sportIdentifier} onChange={(e) => setSportIdentifier(e.target.value)}>
            <option value="">— Sélectionner —</option>
            {sports.map((s) => (
              <option key={s.identifier} value={s.identifier}>
                {s.label}
              </option>
            ))}
          </Select>
          <Select label="Thématique" value={themeId} onChange={(e) => setThemeId(e.target.value)}>
            <option value="">— Sélectionner —</option>
            {themes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <Switch
              checked={effectiveIsTest && sportIdentifier !== 'PPG'}
              onChange={setIsTest}
              size="sm"
              disabled={sportIdentifier === 'PPG' || testFromTheme}
            />
            Séance de test
          </label>
          <p className="text-xs text-gray-400">
            {sportIdentifier === 'PPG'
              ? 'Une séance de PPG ne peut pas être un test.'
              : testFromTheme
                ? 'Déduit de la thématique : une séance du thème Test est une séance de test.'
                : "Toujours classée première dans sa semaine. À regrouper dans des cycles de tests."}
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Textarea
            label="Nomenclature"
            rows={2}
            placeholder={'VO2 : 2 x (8 x 30" @117-123% CP r=20" @55-75% CP) R=3\' @55-75% CP'}
            value={sourcePattern}
            onChange={(e) => setSourcePattern(e.target.value)}
            onBlur={() => triggerDecode(sourcePattern)}
            onPaste={(e) => {
              const el = e.currentTarget;
              setTimeout(() => triggerDecode(el.value), 0);
            }}
          />
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleDecode} disabled={decoding}>
              {decoding ? 'Décodage…' : 'Rejouer'}
            </Button>
            <p className="text-xs text-gray-400">
              Si une nomenclature est saisie, elle prime sur le corps libre.
            </p>
          </div>
          {decodeError && <p className="text-sm text-error-500">{decodeError}</p>}
          {preview && (
            <DecodePreviewTable
              preview={preview}
              themeLabel={themes.find((t) => t.id === themeId)?.label}
              sportIdentifier={sportIdentifier}
            />
          )}
        </div>

        <Textarea
          label="Corps (texte libre, optionnel)"
          rows={2}
          value={contentText}
          onChange={(e) => setContentText(e.target.value)}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <SectionModeField
            label="Échauffement"
            options={[
              { value: 'AUTO', label: 'Thème' },
              { value: 'MANUAL', label: 'Manuel' },
            ]}
            mode={warmupMode}
            onModeChange={setWarmupMode}
            hints={{ AUTO: "Échauffement en fonction du thème." }}
            value={warmupText}
            onChange={setWarmupText}
            placeholder={"15' @Zone1"}
          />
          <SectionModeField
            label="Récupération"
            options={[
              { value: 'IN_BODY', label: 'Corps' },
              { value: 'AUTO', label: 'Thème' },
              { value: 'MANUAL', label: 'Manuel' },
            ]}
            mode={cooldownMode}
            onModeChange={setCooldownMode}
            hints={{
              IN_BODY: 'Incluse dans le corps de séance.',
              AUTO: 'Récupération en fonction du thème.',
            }}
            value={cooldownText}
            onChange={setCooldownText}
            placeholder={"10' @Zone1"}
          />
          <AutoManualField
            label="Mot du coach"
            autoHint="Hérité du contenu de la thématique."
            manual={coachManual}
            onToggle={setCoachManual}
            value={coachNote}
            onChange={setCoachNote}
          />
          <AutoManualField
            label="Nutrition"
            autoHint="Héritée du contenu de la thématique."
            manual={nutritionManual}
            onToggle={setNutritionManual}
            value={nutrition}
            onChange={setNutrition}
          />
        </div>

        <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />

        {isEdit && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Enregistrer supprime puis recrée les intervalles depuis la nomenclature.
          </p>
        )}
        {error && <p className="text-sm text-error-500">{error}</p>}
      </div>
    </Modal>
  );
};

export default WorkoutModal;
